import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { hoursHint, missingTimeNote, unloggedView, type AircraftSummary } from '@/lib/summary';

interface SummaryStats {
  total_flights: number;
  // The API sends this as a number (the hours column is parsed at the driver, see
  // packages/api/src/db/client.ts). It is still coerced below: this one crosses the
  // wire, and JSON is the one boundary the browser cannot type-check.
  total_hours: number;
  flights_without_time: number;
  total_distance_nm: number;
  business_flights: number;
  personal_entertainment_flights: number;
  personal_non_entertainment_flights: number;
}

function StatTile({
  label,
  value,
  accent = 'text-white',
  hint,
}: {
  label: string;
  value: string | number;
  accent?: string;
  hint?: string;
}) {
  return (
    <div className="glass rounded-2xl p-5 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-2 text-4xl font-bold tabular-nums ${accent}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/** A figure inside the aircraft panel — smaller than a hero StatTile. */
function PanelStat({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string | number;
  accent: string;
  hint?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-3xl font-bold tabular-nums ${accent}`}>{value}</p>
      {hint && <p className="mt-1 break-words text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<SummaryStats | null>(null);
  const [summary, setSummary] = useState<AircraftSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // The aircraft numbers come straight from /api/summary — the same summary the
  // co-owner page renders, so the two surfaces cannot disagree.
  const loadSummary = () =>
    apiGet<AircraftSummary>('/api/summary')
      .then(setSummary)
      .catch((err) => console.error('Failed to load the summary:', err));

  useEffect(() => {
    Promise.all([
      fetch('/api/flights/summary', { credentials: 'include' })
        .then((res) => res.json())
        .then(setStats)
        .catch((err) => console.error('Failed to load summary:', err)),
      loadSummary(),
    ]).finally(() => setLoading(false));
  }, []);

  const personal =
    (stats?.personal_entertainment_flights ?? 0) + (stats?.personal_non_entertainment_flights ?? 0);
  const hours = (Number(stats?.total_hours) || 0).toFixed(1);
  const unlogged = unloggedView(summary?.unlogged_flights);
  const missing = missingTimeNote(summary?.my_flights_without_time);

  return (
    <div className="min-h-full">
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden border-b border-white/5">
        {/* Meteorite front-view render, feathered in as a faint watermark */}
        <img
          src="/aircraft-front.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-1/2 hidden w-[62%] max-w-none -translate-y-1/2 select-none opacity-[0.16] sm:block"
          style={{
            WebkitMaskImage: 'radial-gradient(ellipse 70% 90% at 75% 50%, #000 25%, transparent 72%)',
            maskImage: 'radial-gradient(ellipse 70% 90% at 75% 50%, #000 25%, transparent 72%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-night-900 via-night-900/85 to-night-900/30" />

        <div className="relative flex items-end justify-between gap-6 px-8 py-10">
          <div>
            <p className="ident text-sm text-avio">N313G · Cirrus SR22T G7+</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-white">Flight Deck</h1>
            <p className="mt-2 max-w-lg text-slate-400">
              Live logbook, FlightAware tracking, and tax-compliance analytics.
            </p>
          </div>
          <Link to="/flights/new" className="btn-primary shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Log Flight
          </Link>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="p-8">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="glass h-28 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            {/* ── The aircraft ─────────────────────────────────────────
                My flying and the co-owner's, how far, and whatever FlightAware
                has seen but nobody has classified yet. No meter: the Hobbs
                balance went with the engine basis. */}
            {summary && (
              <div className="glass mb-6 rounded-2xl p-5 shadow-card sm:p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                    Flight time &amp; distance
                  </h2>
                  <Link to="/coowner" className="text-xs text-slate-400 hover:text-white">
                    Co-owner &amp; detections →
                  </Link>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                  <PanelStat
                    label="My flight time"
                    value={summary.my_hours.toFixed(1)}
                    accent="text-avio"
                    hint={hoursHint(summary.my_flights, summary.my_flights_without_time)}
                  />
                  <PanelStat
                    label="Distance"
                    value={`${summary.my_distance_nm.toLocaleString()}`}
                    accent="text-white"
                    hint={
                      'nm, great-circle' +
                      (summary.my_local_flights > 0 ? ` · ${summary.my_local_flights} local` : '') +
                      (summary.my_unknown_distance_flights > 0 ? ` · ${summary.my_unknown_distance_flights} unknown` : '')
                    }
                  />
                  <PanelStat
                    label="Co-owner"
                    value={summary.other_hours.toFixed(1)}
                    accent="text-slate-200"
                    hint={hoursHint(summary.other_flights, summary.other_flights_without_time)}
                  />
                  <PanelStat
                    label={unlogged.label}
                    value={unlogged.value}
                    accent={unlogged.accent}
                    hint={unlogged.hint}
                  />
                </div>

                {missing && (
                  <Link
                    to="/flights"
                    className="mt-3 block rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-sm text-amber-200 transition hover:bg-amber-400/10"
                  >
                    {missing}. Open each one and enter it →
                  </Link>
                )}

                {unlogged.state === 'pending' && (
                  <Link
                    to="/flightaware"
                    className="mt-3 block rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-sm text-amber-200 transition hover:bg-amber-400/10"
                  >
                    Classify {unlogged.value} detected {unlogged.value === 1 ? 'flight' : 'flights'} →
                  </Link>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile label="Total Flights" value={stats?.total_flights ?? 0} />
              <StatTile
                label="Flight Time"
                value={hours}
                accent="text-avio"
                hint={stats?.flights_without_time ? `h · ${stats.flights_without_time} legs not entered yet` : 'h · my flights'}
              />
              <StatTile label="Business" value={stats?.business_flights ?? 0} accent="text-ember" hint="Deductible" />
              <StatTile label="Personal" value={personal} accent="text-slate-200" />
            </div>

            {/* Quick actions */}
            <div className="glass mt-6 rounded-2xl p-6 shadow-card">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Quick Actions</h2>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/flights/new" className="btn-primary">Log New Flight</Link>
                <Link to="/flights" className="btn-ghost">View Flight Log</Link>
                <Link to="/flightaware" className="btn-ghost">FlightAware Monitor</Link>
                <Link to="/analytics" className="btn-ghost">Tax Analytics</Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
